/**
 * HCaptcha Captcha Provider Implementation
 *
 * This module provides an implementation of the hCaptcha invisible verification service.
 * It handles SDK loading, widget initialization, token generation, and comprehensive error handling.
 *
 */

import Captcha from '../Captcha';
import {debugMode} from '@/constants/environment';
import {CbError} from '@/hosted_fields/common/errors';
import t from '@/hosted_fields/common/locale';
import ErrorCodes from '@/hosted_fields/common/errors';
import {jsonify} from '@/utils/utility-functions';
import {HCaptchaWindow, HCaptchaRenderOptions, HCaptchaLoaderOptions, HCaptchaExecuteResponse} from './types';
import {extractErrorMessage, extractErrorName} from './utils';
import {EXECUTION_ERRORS, RENDER_ERRORS, CALLBACK_ERRORS, ERROR_CONTEXT} from './errors';
import '@hcaptcha/loader/dist/polyfills.js';
import '@hcaptcha/loader/dist/index.es5.js';

/**
 * HCaptcha implementation for invisible captcha verification
 * Handles loading the hCaptcha SDK and executing token generation with proper error handling
 */
export default class HCaptcha extends Captcha {
  private widgetId: string = '';
  private hcaptcha: HCaptchaWindow | null = null;

  /**
   * Load the hCaptcha SDK and initialize the widget
   */
  protected async load(): Promise<void> {
    const container = this.createContainer();

    try {
      // Try to load hCaptcha using the loader
      const loaderOptions: HCaptchaLoaderOptions = {
        scriptLocation: document.body,
      };

      if (this.getSdkEndpoint()) {
        loaderOptions.scriptSource = this.getSdkEndpoint();
      }

      this.debugLog('Loading hCaptcha with loader options', {endpoint: this.getSdkEndpoint()});
      this.hcaptcha = await window.hCaptchaLoader(loaderOptions);
      this.widgetId = await this.render(container);
    } catch (loaderError) {
      // Fallback: Load hCaptcha script directly and wait for callback
      this.debugLog('hCaptcha loader failed, attempting script load fallback', {error: loaderError});

      const onLoadPromise = new Promise<void>((resolve) => {
        window.hCaptchaLoadCallback = resolve;
      });

      await this.loadScript();
      await onLoadPromise;

      this.hcaptcha = window.hcaptcha;
      this.widgetId = await this.render(container);
    }
  }

  /**
   * Execute hCaptcha token generation
   * Uses async/await for better readability and error handling
   */
  protected async execute(action?: string): Promise<string> {
    if (!this.hcaptcha) {
      const errorMsg = EXECUTION_ERRORS.NOT_INITIALIZED;
      this.debugLog('execute: hCaptcha not initialized');
      throw new CbError(errorMsg);
    }

    if (!this.widgetId) {
      const errorMsg = EXECUTION_ERRORS.WIDGET_ID_MISSING;
      this.debugLog('execute: widget ID missing');
      throw new CbError(errorMsg);
    }

    try {
      this.debugLog('execute: Starting token generation', {widgetId: this.widgetId, action});

      const result = await this.hcaptcha.execute(this.widgetId, {async: true});

      if (!result || !result.response) {
        const errorMsg = EXECUTION_ERRORS.NO_RESPONSE_RECEIVED;
        this.debugLog('execute: Empty response from hCaptcha');
        this.handleError('execute_failed', errorMsg);
        throw new CbError(errorMsg);
      }

      this.handleSuccess(result.response, action);
      return result.response;
    } catch (error) {
      // Distinguish between expected CbError and unexpected errors
      if (error instanceof CbError) {
        this.debugLog('execute: CbError caught', {message: error.message});
        this.handleError('execute_error', error);
        throw error;
      }

      // Handle unexpected errors
      const errorMessage = extractErrorMessage(error);
      const errorName = extractErrorName(error);

      this.debugLog('execute: Exception caught', {errorName, errorMessage});

      this.handleError('execute_exception', error);
      throw new CbError(errorMessage);
    }
  }

  /**
   * Get the hCaptcha script URL
   */
  protected getScriptURL(): string {
    return 'https://js.hcaptcha.com/1/api.js?onload=hCaptchaLoadCallback&render=explicit';
  }

  /**
   * Render hCaptcha widget in the provided container
   * Uses async/await for sequential promise handling
   */
  private async render(container: HTMLDivElement): Promise<string> {
    if (!this.hcaptcha) {
      const errorMsg = RENDER_ERRORS.SDK_NOT_AVAILABLE;
      this.debugLog('render: hCaptcha SDK not available');
      throw new CbError(errorMsg);
    }

    try {
      const siteKey = this.getSiteKey();
      this.debugLog('render: Rendering hCaptcha widget', {siteKey: (siteKey ? siteKey.substring(0, 15) : '') + '...'});

      const renderOptions: HCaptchaRenderOptions = {
        sitekey: this.getSiteKey(),
        callback: (token: string) => this.handleCallback(token, 'success'),
        'error-callback': (error: unknown) => this.handleCallback(extractErrorMessage(error), 'error'),
        'expired-callback': () => this.handleCallback(CALLBACK_ERRORS.TOKEN_EXPIRED, 'expired'),
        'chalexpired-callback': () => this.handleCallback(CALLBACK_ERRORS.CHALLENGE_EXPIRED, 'chalexpired'),
        'open-callback': () => this.handleCallback('opened', 'opened'),
        'close-callback': () => this.handleCallback('closed', 'closed'),
      };

      const widgetId = this.hcaptcha.render(container, renderOptions);

      if (widgetId === undefined || widgetId === null) {
        const errorMsg = RENDER_ERRORS.INVALID_WIDGET_ID;
        this.debugLog('render: Widget ID is invalid', {widgetId});
        throw new CbError(errorMsg);
      }

      this.debugLog('render: Widget rendered successfully', {widgetId});
      return String(widgetId);
    } catch (error) {
      // Distinguish between expected CbError and unexpected errors
      if (error instanceof CbError) {
        this.debugLog('render: CbError caught', {message: error.message});
        this.reportErrorEvent({
          action: 'cbjs_recaptcha_error',
          error_message: error.message,
          error_name: error.name,
          hp_step: 'hcaptcha_render',
          captcha_config: this.options,
        });
        throw error;
      }

      // Handle unexpected errors
      const errorMessage = extractErrorMessage(error);
      const errorName = extractErrorName(error);

      this.debugLog('render: Exception caught', {errorName, errorMessage});

      this.reportErrorEvent({
        action: 'cbjs_recaptcha_error',
        error_message: errorMessage,
        error_name: errorName,
        hp_step: 'hcaptcha_render',
        captcha_config: this.options,
      });

      throw new CbError(errorMessage);
    }
  }

  /**
   * Handle hCaptcha callbacks (success, error, expiry, etc.)
   * Reports non-trivial events to the configured error handler
   *
   * Note: 'success' and 'error' callbacks are already logged in execute() method,
   * so we only log async events (expired, chalexpired) to avoid duplicate logging
   */
  private handleCallback(value: string, callbackType: string): void {
    this.debugLog('handleCallback', {callbackType, value: callbackType === 'success' ? '[token]' : value});

    // Only log async error events that aren't already handled in execute()
    // - Skip 'opened' and 'closed' (UI lifecycle events)
    // - Skip 'success' and 'error' (already logged in execute())
    // - Log 'expired' and 'chalexpired' (async error events outside execute flow)
    const asyncErrorEvents = ['expired', 'chalexpired'];
    if (asyncErrorEvents.includes(callbackType)) {
      this.reportErrorEvent({
        action: 'cbjs_recaptcha_error',
        error_message: value,
        error_name: callbackType,
        callback_type: callbackType,
        hp_step: `hcaptcha_${callbackType}`,
        captcha_provider: 'hcaptcha',
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Handle successful token generation
   * Reports metrics about successful token generation
   */
  private handleSuccess(token: string, action?: string): void {
    this.debugLog('handleSuccess', {
      action,
      tokenLength: token.length,
      tokenHash: this.hashToken(token),
    });

    this.reportEventData({
      action: 'hcaptcha_token_success',
      hp_step: 'hcaptcha_token_generated',
      callback_type: 'success',
      captcha_provider: 'hcaptcha',
      token_hash: this.hashToken(token),
      siteKey: this.getSiteKey() ? this.getSiteKey().substring(0, 15) + '...' : 'unknown',
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Handle hCaptcha errors
   * Reports error details to configured error handler with proper type safety
   */
  private handleError(callbackType: string, error: unknown): void {
    const errorMessage = extractErrorMessage(error);
    const errorName = extractErrorName(error);

    this.debugLog('handleError', {callbackType, errorName, errorMessage});

    this.reportErrorEvent({
      action: 'cbjs_recaptcha_error',
      error_message: errorMessage,
      error_name: errorName,
      callback_type: callbackType,
      hp_step: `hcaptcha_${callbackType}`,
      captcha_provider: 'hcaptcha',
      captcha_config: this.options,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Report error events to the configured error handler
   */
  private reportErrorEvent(data: Record<string, any>): void {
    try {
      // Call onError for backwards compatibility
      if (this.options.onError && typeof this.options.onError === 'function') {
        this.options.onError(data);
      }
      // Call logToKVL for structured logging
      if (this.options.logToKVL && typeof this.options.logToKVL === 'function') {
        this.options.logToKVL(data);
      }
    } catch (e) {
      this.debugLog('reportErrorEvent: Exception in callback', {error: e});
    }
  }

  /**
   * Report event data to the configured handlers
   * Calls both onError (for backwards compatibility) and logToKVL (for structured logging)
   */
  private reportEventData(data: Record<string, any>): void {
    try {
      // Call onError for backwards compatibility
      if (this.options.onError && typeof this.options.onError === 'function') {
        this.options.onError(data);
      }
      // Call logToKVL for structured logging
      if (this.options.logToKVL && typeof this.options.logToKVL === 'function') {
        this.options.logToKVL(data);
      }
    } catch (e) {
      this.debugLog('reportEventData: Exception in callback', {error: e});
    }
  }

  /**
   * Create a hash of the token for logging purposes
   * This allows tracking token generation without exposing full tokens
   */
  private hashToken(token: string): string {
    try {
      if (!token || typeof token !== 'string') {
        return 'invalid_token';
      }

      const first10 = token.substring(0, 10);
      const last10 = token.substring(Math.max(0, token.length - 10));
      return `${first10}...${last10}(len:${token.length})`;
    } catch (e) {
      this.debugLog('hashToken: Exception during hashing', {error: e});
      return 'hash_error';
    }
  }

  /**
   * Create and configure the hCaptcha container element
   */
  private createContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.id = 'captcha_container';
    container.setAttribute('class', container.id);
    container.setAttribute('data-size', 'invisible');
    document.body.appendChild(container);

    this.debugLog('createContainer: Container created', {containerId: container.id});

    return container;
  }

  /**
   * Internal debug logging utility
   * Only logs when debug mode is enabled to avoid console spam
   */
  private debugLog(method: string, details?: Record<string, any>): void {
    if (debugMode()) {
      const logPrefix = '[HCaptcha]';
      if (details) {
        console.log(`${logPrefix} ${method}`, details);
      } else {
        console.log(`${logPrefix} ${method}`);
      }
    }
  }
}
